<script setup lang="ts">
import { RouteGuards } from "@/router/guards";
import { useBaseStore } from "@/stores/base";
import { computed, onBeforeMount, ref } from "vue";
import { useApiClient } from "@/api/client";
import { onBeforeRouteUpdate, useRouter } from "vue-router";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { t } from "@/plugins/i18n";
import { useCurveModelStore } from "@/modules/booking_experts/store";

const store = useBaseStore();
const curveModelStore = useCurveModelStore();

const isLoading = ref(true);
const activeTab = ref("general");

const activeTabObject = computed(() => {
	return tabs.find((tab) => tab.itemId === activeTab.value);
});

type TabsType = {
	itemId: string;
	translationKey: string;
	icon: string;
	to: string;
	requires_administration_select: boolean;
};

const tabs: TabsType[] = [
	{
		itemId: "general",
		translationKey: "pricelist.tabs.general",
		icon: "fa-calendars",
		to: "/pricing/pricelists",
		requires_administration_select: true,
	},
	{
		itemId: "curves",
		translationKey: "pricelist.tabs.curves",
		icon: "fa-chart-line",
		to: "/pricing/curves",
		requires_administration_select: true,
	},
	{
		itemId: "bookings",
		translationKey: "pricelist.tabs.bookings",
		icon: "fa-house-person-return",
		to: "/pricing/bookings",
		requires_administration_select: true,
	},
];

const breadcrumbs = computed(() => {
	return [
		{
			title: "Pricing",
			disabled: false,
			to: "/pricing",
		},
		{
			title: t(activeTabObject.value.translationKey),
			disabled: currentRoute.value.path === activeTabObject.value.to,
			to: activeTabObject.value.to,
		},
		...(curveModelStore.currentBreadcrumb
			? [
					{
						title: curveModelStore.currentBreadcrumb,
						disabled: true,
						to: null,
					},
			  ]
			: []),
	];
});

const fetchAdministrations = async () => {
	const response = await useApiClient("bookingExperts").getAdministrations();
	if (response.isOk()) {
		store.bexAdministrations = response.value;
	}
};

async function loadData() {
	await fetchAdministrations();
	if (store.bexAdministrations.length > 0 && !store.selectedBexAdministration) {
		store.selectedBexAdministration = store.bexAdministrations[0];
	}
	isLoading.value = false;
}

onBeforeMount(loadData);
// NOTE(Leon): Ensure that subpages are also synced so that the bexAdmin id is set.
onBeforeRouteUpdate(loadData);

const currentRoute = useRouter().currentRoute;

const validAdministration = computed({
	get() {
		return store.selectedBexAdministration;
	},
	set(value) {
		if (!value) {
			return;
		}

		store.selectedBexAdministration = value;
	},
});

const administrations = computed(() => {
	return activeTab.value === "curves"
		? [{ id: -1, name: "All" }, ...store.bexAdministrations]
		: store.bexAdministrations;
});
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />

	<div v-if="!isLoading">
		<template v-if="store.selectedBexAdministration">
			<div class="d-flex align-center bg-secondary">
				<v-tabs v-model="activeTab">
					<v-tab
						v-for="(tab, i) in tabs"
						:key="i"
						v-show="
							RouteGuards.hasPermissions({
								to: RouteGuards.getRouteByPath(tab.to),
								store,
							})
						"
						:value="tab.itemId"
						color="primary"
						:to="tab.to"
					>
						<i :class="`fa-duotone ${tab.icon} fa-lg`"></i>
						<div class="tab-text">
							{{ t(tab.translationKey) }}
						</div>
					</v-tab>
				</v-tabs>
				<v-spacer />
				<v-autocomplete
					v-if="activeTabObject.requires_administration_select"
					prepend-inner-icon="mdi-home-switch"
					v-model="validAdministration"
					:items="administrations"
					class="mr-1"
					hide-details
					placeholder="Select Administration"
					variant="filled"
					bg-color="secondary"
					density="compact"
					width="150"
					item-value="id"
					item-title="name"
					return-object
					:disabled="currentRoute.path !== activeTabObject.to"
				/>
				<!-- Ugly hack such that the input is disabled on the pricelist detail page -->
			</div>
			<router-view />
		</template>
		<template v-else>
			<v-alert color="error">
				This environment does not have any active Bex Administrations.
			</v-alert>
		</template>
	</div>
</template>

<style></style>
